import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faEye, faTrash, faCamera } from '@fortawesome/free-solid-svg-icons';
import useFetch from "../../../../hooks/useFetch";
import RequireRoles from '../../../../components/RequireRoles';
import PaginatedItems from '../../../../components/administracao/Pagination';
import Swal from 'sweetalert2';

export default function GerenciarInventariosIconografia() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        inventarios: [],
        count: 0,
        loading: true,
        mostrarModal: false,
        registroAExcluir: {}
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [inventarios, count] = await buscarInventarios(1);
            setState(s => ({ ...s, inventarios, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarInventarios(page) {
        try {
            let response = await get(`/inventariofotografia?page=${page ?? 1}`);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarInventarios():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let [inventarios, count] = await buscarInventarios(pageNumber);
        setState({ ...state, inventarios, count, loading: false });
    }

    async function excluirInventario() {
        try {
            let response = await del('/inventariofotografia/' + state.registroAExcluir.id_inventario);
            if (response.status === 200) {
                let [inventarios, count] = await buscarInventarios(1);
                setState({ ...state, inventarios, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else {
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirInventario():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Inventários Iconográficos</h2>
                    <p className="admin-subtitle">Consulte e organize os registros de inventário das fotografias e documentos</p>
                </div>
                <Link to="/admin/iconografico/inventarios/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Inventário
                </Link>
            </div>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Data</th>
                                <th className="text-center">Fotografias</th>
                                <th>Observação</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.inventarios.length > 0 ? state.inventarios.map((item) => (
                                <tr key={item.id_inventario}>
                                    <td data-label="Usuário" className="fw-bold">{item.Usuario?.nome_pessoa ?? item.Usuario?.nome_usuario ?? '-'}</td>
                                    <td data-label="Data" className="text-muted">{item.data_inventario ? new Date(item.data_inventario).toLocaleDateString('pt-BR') : '-'}</td>
                                    <td data-label="Fotografias" className="text-center">
                                        <span className="badge bg-light text-dark border">
                                            <FontAwesomeIcon icon={faCamera} className="me-1 text-teal" />
                                            {item.Fotografia?.length ?? 0}
                                        </span>
                                    </td>
                                    <td data-label="Observação" className="text-muted small">{item.observacoes || '-'}</td>
                                    <td data-label="Opções">
                                        <div className="d-flex justify-content-center gap-2">
                                            <Link className="action-icon view" to={`/admin/iconografico/inventarios/${item.id_inventario}`} state={{ acao: "Visualizar" }}><FontAwesomeIcon icon={faEye} /></Link>
                                            <Link className="action-icon edit" to={`/admin/iconografico/inventarios/${item.id_inventario}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                            <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                            </RequireRoles>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="text-center py-4">Nenhum inventário encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.inventarios.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Inventário</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir este registro de inventário? As fotografias em si não serão afetadas, só deixam de constar nesse inventário.</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirInventario}>Confirmar Exclusão</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
